import React, { useContext, useState, useMemo, useEffect } from 'react';
import classnames from 'classnames';
import { searchScopes } from '@worklist-2/core/src/context/consts/searchScopes';
import { faxIsValidToDisplayPDF } from '@rs-core/functions/fax';
import { useTranslation } from 'react-i18next';
import { FaxContext } from '@rs-ui/context/FaxContext';
import useFhirAPI from '@rs-core/hooks/useFhirAPI';
import { useBooleanFlagValue } from '@rs-core/hooks/useFlags';
import { logError } from '@rs-core/utils/logger';
import RsGrid from '@rs-ui/components/Grid';

const faxReadUnreadFilterOption = {
	displayValue: ['unread'],
	label: 'Read/Unread',
	columnIdentity: 'readUnreadFax',
	values: ['unread'],
	capsuleMenuItems: [
		[
			{ code: 'all', display: 'all' },
			{ code: 'read', display: 'read' },
			{ code: 'unread', display: 'unread' },
		],
	],
	searchParameter: 'readUnreadFax',
	searchValueSet: 'readUnreadFax',
	filterType: 'single-select',
};

export const updateFaxReadBoolean = ({ dataGridRefValue, selectedFaxLog }) => {
	const faxData = dataGridRefValue?.current?.getRowData('id', selectedFaxLog?.id);
	if (faxData?.rawData?.isRead === false) {
		const updatedData = {
			id: faxData.id,
			data: {
				...faxData,
				rawData: {
					...faxData.rawData,
					isRead: true,
				},
			},
		};
		dataGridRefValue?.current?.insertUpdateRow(updatedData);
	}
};

const FaxLogGrid = ({ className, name, columns, filters, setFilters }) => {
	const { t } = useTranslation('fax');
	const [sort, setSort] = useState([]);
	const { selectedFaxLog, setSelectedFaxLog, dataGridRefValue, setDataGridRefValue } = useContext(FaxContext);

	const { markFaxAsRead } = useFhirAPI();

	const galaxyDVReadUnreadFax = useBooleanFlagValue('galaxy-dv-read-unread-fax');

	useEffect(() => {
		if (!galaxyDVReadUnreadFax) return;

		const isFaxRead = selectedFaxLog?.apiRawData?.isRead;

		if (selectedFaxLog && isFaxRead === false) {
			updateFaxReadBoolean({ dataGridRefValue, selectedFaxLog });
		}
	}, [selectedFaxLog]);

	const handleRowSelect = async rowData => {
		if (!rowData) return;

		const { id, messageId, faxDirection, faxDatetime, faxFrom, status, isRead } = rowData.rawData;
		const alreadySelected = selectedFaxLog?.id === rowData.rawData.id;
		const faxHasValidPDF = faxIsValidToDisplayPDF(rowData.rawData);

		if (faxHasValidPDF && !alreadySelected) {
			setSelectedFaxLog({
				id,
				messageId,
				faxDirection,
				faxDatetime,
				faxFrom,
				status,
				apiRawData: rowData.rawData,
			});

			if (galaxyDVReadUnreadFax && isRead === false) {
				try {
					await markFaxAsRead(id);
				} catch (err) {
					logError('failed to mark fax read.', err);
				}
			}
		} else {
			setSelectedFaxLog(null);
		}
	};

	const hasOutbound = filters.some(item => Array.isArray(item.values) && item.values.includes('outbound'));

	const updatedColumns = useMemo(() => {
		if (!galaxyDVReadUnreadFax || (galaxyDVReadUnreadFax && hasOutbound)) {
			return columns.filter(column => column !== 'readUnreadFax');
		}

		return ['readUnreadFax', ...columns];
	}, [columns, galaxyDVReadUnreadFax, hasOutbound]);

	const updatedFilter = useMemo(() => {
		if (!galaxyDVReadUnreadFax || (galaxyDVReadUnreadFax && hasOutbound)) {
			return filters.filter(item => item.columnIdentity !== 'readUnreadFax');
		}

		if (!filters.some(item => item.columnIdentity === 'readUnreadFax')) {
			return [...filters, { ...faxReadUnreadFilterOption }];
		}

		return filters;
	}, [filters, galaxyDVReadUnreadFax, hasOutbound]);

	return (
		<RsGrid
			isSingleSort
			selectableRows
			className={classnames(className)}
			columns={updatedColumns}
			enableRowDnd={false}
			fetchInterval={15000}
			filters={updatedFilter}
			name={name}
			scope={searchScopes.fax}
			setDataGridRefValue={setDataGridRefValue}
			setFilters={setFilters}
			setSort={setSort}
			sort={sort}
			title={t('Fax Log')}
			onRowSelect={handleRowSelect}
		/>
	);
};

export default FaxLogGrid;
